import { useEffect } from 'react';
import { useUIStore } from '@/stores/uiStore';
import type { ToastNotification } from '@/types';

export function Toaster() {
  const { notifications, removeNotification } = useUIStore();

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-toast flex flex-col gap-2 pointer-events-none">
      {notifications.map((notification) => (
        <Toast
          key={notification.id}
          notification={notification}
          onDismiss={() => removeNotification(notification.id)}
        />
      ))}
    </div>
  );
}

interface ToastProps {
  notification: ToastNotification;
  onDismiss: () => void;
}

function Toast({ notification, onDismiss }: ToastProps) {
  useEffect(() => {
    if (notification.duration && notification.duration > 0) {
      const timer = setTimeout(onDismiss, notification.duration);
      return () => clearTimeout(timer);
    }
  }, [notification.duration, onDismiss]);

  const getIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckIcon className="w-5 h-5 text-secondary-500" />;
      case 'error':
        return <ErrorIcon className="w-5 h-5 text-danger-500" />;
      case 'warning':
        return <WarningIcon className="w-5 h-5 text-warning-500" />;
      default:
        return <InfoIcon className="w-5 h-5 text-primary-500" />;
    }
  };

  const getBgColor = () => {
    switch (notification.type) {
      case 'success':
        return 'bg-secondary-50 border-secondary-200';
      case 'error':
        return 'bg-danger-50 border-danger-200';
      case 'warning':
        return 'bg-warning-50 border-warning-200';
      default:
        return 'bg-white border-gray-200';
    }
  };

  return (
    <div
      className={`
        flex items-center gap-3 px-4 py-3 rounded-lg border shadow-card
        pointer-events-auto animate-slide-up ${getBgColor()}
      `}
    >
      {getIcon()}
      <span className="text-sm text-gray-700">{notification.message}</span>
      <button
        onClick={onDismiss}
        className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
      >
        <CloseIcon className="w-4 h-4" />
      </button>
    </div>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ErrorIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function WarningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
  );
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function CloseIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
